import React, { useEffect, useState, useRef, useMemo } from "react";

const AnimatedTagline = () => {
  const [currentPhase, setCurrentPhase] = useState(0);
  const [animationState, setAnimationState] = useState("visible"); // "visible", "exiting", "entering"
  const [currentBullet, setCurrentBullet] = useState(0); // Track the currently visible bullet point
  const [bulletVisible, setBulletVisible] = useState(false); // Track if bullet point is visible
  const timerRef = useRef(null);

  // Wrap phrases array in useMemo to prevent recreation on every render
  const firstTwoLines = useMemo(
    () => ["Find Your Dream Career.", "Simple. Innovative. Precise."],
    []
  );

  // Bullet points for the third phase - shortened for mobile compatibility
  const bulletPoints = useMemo(
    () => [
      "Experience smarter career exploration.",
      "Align skills with opportunities.",
      "Get your resume optimized.",
      "Apply in seconds.",
    ],
    []
  );

  // For letter animation
  const [visibleLetters, setVisibleLetters] = useState(0);
  const textRef = useRef("");

  // Track if second line is active and transitioning from bottom
  const [secondLineActive, setSecondLineActive] = useState(false);

  useEffect(() => {
    // Reset letter animation whenever phase changes
    if (currentPhase < 2) {
      textRef.current = firstTwoLines[currentPhase];
      setVisibleLetters(0);

      // If we're entering the second phase, set the second line active flag
      if (currentPhase === 1) {
        setSecondLineActive(true);
      }

      // Use requestAnimationFrame for smoother animation
      let startTime = null;
      const duration = 1200; // Total duration for all letters to appear (ms)

      const animateLetters = (timestamp) => {
        if (!startTime) startTime = timestamp;
        const elapsedTime = timestamp - startTime;

        // Calculate how many letters should be visible based on elapsed time
        const progress = Math.min(elapsedTime / duration, 1);
        const lettersToShow = Math.floor(progress * textRef.current.length);

        setVisibleLetters(lettersToShow);

        if (lettersToShow < textRef.current.length) {
          requestAnimationFrame(animateLetters);
        }
      };

      const animationFrame = requestAnimationFrame(animateLetters);

      return () => cancelAnimationFrame(animationFrame);
    } else {
      // For bullet points, reset and start with the first bullet point
      setCurrentBullet(0);
      setBulletVisible(true);
    }
  }, [currentPhase, firstTwoLines, bulletPoints]);

  // Handle cycling through bullet points
  useEffect(() => {
    if (currentPhase !== 2) {
      return;
    }

    if (bulletVisible) {
      // After displaying a bullet for some time, hide it
      const hideTimer = setTimeout(() => {
        setBulletVisible(false);
      }, 3000); // Show each bullet for 3 seconds

      return () => clearTimeout(hideTimer);
    } else {
      // After hiding a bullet, move to the next one
      const nextTimer = setTimeout(() => {
        // Move to the next bullet or loop back to the first one
        setCurrentBullet((prev) => (prev + 1) % bulletPoints.length);
        setBulletVisible(true);
      }, 500); // Wait 0.5 second between bullets

      return () => clearTimeout(nextTimer);
    }
  }, [currentPhase, bulletVisible, currentBullet, bulletPoints.length]);

  useEffect(() => {
    if (animationState === "exiting" || animationState === "entering") {
      return;
    }

    // For first two phases, wait for letter animation
    if (currentPhase < 2 && visibleLetters < textRef.current.length) {
      return;
    }

    // For the third phase, check if we've gone through all bullet points
    if (currentPhase === 2) {
      // If we've shown all bullet points and the last one is no longer visible
      if (currentBullet === bulletPoints.length - 1 && !bulletVisible) {
        // Wait 2 seconds before starting the loop over
        const loopTimer = setTimeout(() => {
          setAnimationState("exiting");
        }, 2000); // Added 2-second pause before restarting

        return () => clearTimeout(loopTimer);
      }
    } else {
      // For first two phases, use normal timing
      const displayDuration = 4000;

      timerRef.current = setTimeout(() => {
        setAnimationState("exiting");
      }, displayDuration);
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [
    currentPhase,
    animationState,
    visibleLetters,
    currentBullet,
    bulletVisible,
    bulletPoints.length,
  ]);

  useEffect(() => {
    // Handle animation state transitions
    if (animationState === "exiting") {
      const timer = setTimeout(() => {
        // If we're at the last phase (bullets), loop back to the first phase
        if (currentPhase === 2) {
          setCurrentPhase(0); // Reset to the first phase
        } else {
          // Otherwise, progress to the next phase
          setCurrentPhase((prev) => prev + 1);
        }
        setAnimationState("entering");
      }, 500); // Duration of exit animation

      return () => clearTimeout(timer);
    } else if (animationState === "entering") {
      const timer = setTimeout(() => {
        setAnimationState("visible");
      }, 500); // Duration of enter animation

      return () => clearTimeout(timer);
    }
  }, [animationState, currentPhase]);

  // Memoize the rendered character spans for first two phases
  const renderCharacters = useMemo(() => {
    if (currentPhase >= 2) return null;

    const text = firstTwoLines[currentPhase];
    return text.split("").map((char, index) => {
      // Calculate a base delay that's proportional to the index
      // but caps at a maximum to prevent excessive delays for longer text
      const maxIndex = Math.min(index, 10);
      const swingDelay = maxIndex * 30; // ms of delay per character, max 300ms

      // For the second line, add a transform that starts from bottom
      const secondLineStartTransform =
        currentPhase === 1 && secondLineActive
          ? "translateY(20px)"
          : "translateX(30px) rotate(8deg)";

      return (
        <span
          key={index}
          style={{
            opacity: index < visibleLetters ? 1 : 0,
            display: "inline-block",
            transform:
              index < visibleLetters
                ? "translateY(0) rotate(0deg)"
                : secondLineStartTransform,
            transition: `opacity 50ms ease, transform 300ms cubic-bezier(0.175, 0.885, 0.32, 1.275) ${swingDelay}ms`,
            fontWeight: currentPhase === 0 ? 600 : 500,
          }}
        >
          {char === " " ? "\u00A0" : char}
        </span>
      );
    });
  }, [currentPhase, visibleLetters, firstTwoLines, secondLineActive]);

  // Dynamically determine styles based on current phase
  const getStyles = () => {
    const baseStyles = {
      fontFamily: "Poppins, sans-serif",
      opacity: 1,
      transform: "translateX(0)",
      transition: "opacity 500ms ease, transform 500ms ease",
    };

    // Add exit and enter animations
    if (animationState === "exiting") {
      baseStyles.opacity = 0;
      baseStyles.transform = "translateX(-50px)";
    } else if (animationState === "entering") {
      baseStyles.opacity = 0;
      baseStyles.transform = "translateX(50px)";
    }

    if (currentPhase === 0) {
      return {
        ...baseStyles,
        fontWeight: 600,
        fontSize: "clamp(18px, 4vw, 28px)",
        lineHeight: 1.3,
        color: "#004D29",
        marginBottom: "clamp(8px, 2vw, 16px)",
        textShadow: "0px 2px 4px rgba(0, 77, 41, 0.1)",
      };
    } else if (currentPhase === 1) {
      return {
        ...baseStyles,
        fontWeight: 500,
        fontSize: "clamp(16px, 3vw, 22px)",
        lineHeight: 1.3,
        color: "#0A633C",
        marginBottom: "clamp(12px, 2vw, 20px)",
      };
    } else {
      return {
        ...baseStyles,
        fontWeight: 400,
        fontSize: "clamp(14px, 2.5vw, 16px)",
        lineHeight: 1.6,
        color: "#333333",
        maxWidth: "90%",
        margin: "0 auto",
        textAlign: "center", // Changed from left to center
      };
    }
  };

  // Render current bullet point with swing-in and swing-out animations
  const renderCurrentBullet = () => {
    if (currentPhase !== 2 || !bulletPoints[currentBullet]) {
      return null;
    }

    return (
      <div
        className="flex items-center" // Keep items vertically centered
        style={{
          opacity: bulletVisible ? 1 : 0,
          transform: bulletVisible ? "translateX(0)" : "translateX(50px)",
          transition: `opacity 500ms ease, transform 500ms cubic-bezier(0.175, 0.885, 0.32, 1.275)`,
          width: "fit-content", // Changed to fit-content to allow centering
          maxWidth: "90%", // Maximum width constraint
          margin: "0 auto", // Center the container horizontally
          padding: "0 10px", // Padding to prevent text touching edges
          fontSize: "clamp(12px, 3vw, 16px)", // Responsive font size
          display: "inline-flex", // Use inline-flex for proper alignment
          justifyContent: "center", // Center the bullet and text as a unit
        }}
      >
        <span
          style={{
            color: "#004D29",
            marginRight: "8px",
            display: "inline-flex",
            alignItems: "center", // Center bullet vertically
            flexShrink: 0, // Prevent bullet from shrinking
            fontSize: "16px", // Slightly larger bullet
            lineHeight: 1, // Tighter line height for bullet
          }}
        >
          •
        </span>
        <span
          style={{
            display: "inline-block",
            textAlign: "left", // Keep text left-aligned relative to bullet
            lineHeight: 1.4, // Better line height for readability
          }}
        >
          {bulletPoints[currentBullet]}
        </span>
      </div>
    );
  };

  // Display text with animated letters
  const renderText = () => {
    if (currentPhase < 2) {
      return <div>{renderCharacters}</div>;
    } else {
      // For bullet points, center the container
      return (
        <div className="w-full flex justify-center">
          {renderCurrentBullet()}
        </div>
      );
    }
  };

  return (
    <div className="w-full overflow-hidden">
      <div
        style={getStyles()}
        className="text-center min-h-[180px] flex items-center justify-center"
      >
        {renderText()}
      </div>
    </div>
  );
};

export default AnimatedTagline;
